import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

export default function Footer() {
  const [channelsOpen, setChannelsOpen] = useState(false);
  const phoneHref = "tel:+79140914902";
  const emailHref = "mailto:aisakh@zmncraft.ru";

  return (
    <>
      <footer className="border-t border-gray-200 dark:border-gray-800 bg-white/50 dark:bg-black/40 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="grid gap-5 text-center sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center sm:text-left">
            {/* Левый блок: копирайт + реквизиты + email */}
            <div className="text-sm text-gray-700 dark:text-gray-300 w-full sm:w-auto sm:justify-self-start sm:text-left">
              <div className="select-none">
                © ИП Зимин В.А., 2025–{new Date().getFullYear()}
              </div>

              <div className="mt-2 space-y-1 text-gray-600 dark:text-gray-400">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    Сахалинская область
                  </span>
                </div>

                <div>
                  <a
                    href={phoneHref}
                    className="font-medium text-gray-700 dark:text-gray-300 hover:text-violet-700 dark:hover:text-violet-300"
                    aria-label="Позвонить по телефону +7 914 091-49-02"
                  >
                    +7 (914) 091-49-02
                  </a>
                </div>

                <div>
                  <a
                    href={emailHref}
                    className="font-medium text-gray-700 dark:text-gray-300 hover:text-violet-700 dark:hover:text-violet-300"
                    aria-label="Написать на email aisakh@zmncraft.ru"
                  >
                    aisakh@zmncraft.ru
                  </a>
                </div>
              </div>
            </div>

            <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm sm:justify-self-center">
              <Link
                to="/about"
                className="font-semibold text-violet-700 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-200"
              >
                О нас
              </Link>
              <Link
                to="/contacts"
                className="font-semibold text-violet-700 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-200"
              >
                Контакты
              </Link>
              <Link
                to="/faq"
                className="font-semibold text-violet-700 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-200"
              >
                FAQ
              </Link>
              <Link
                to="/cases"
                className="font-semibold text-violet-700 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-200"
              >
                Кейсы
              </Link>
            </nav>

            {/* Правый блок: единая кнопка выбора каналов */}
            <div className="flex items-center justify-center w-full sm:w-auto sm:justify-self-end">
              <button
                type="button"
                onClick={() => setChannelsOpen(true)}
                className="
                  inline-flex items-center gap-3
                  rounded-2xl
                  px-3 py-2
                  border border-violet-200/70 dark:border-violet-400/20
                  hover:bg-violet-50/60 dark:hover:bg-white/5
                  transition
                  max-w-full
                  mx-auto
                  text-left
                "
                aria-haspopup="dialog"
                aria-expanded={channelsOpen}
              >
                <span
                  className="
                    inline-flex items-center justify-center
                    h-11 w-11 rounded-2xl
                    text-white
                    bg-gradient-to-r from-cyan-400 via-violet-600 to-violet-950
                    shadow-sm
                    motion-safe:hover:scale-[1.03]
                    transition-transform
                    shrink-0
                  "
                  aria-hidden="true"
                >
                  <ChannelsIcon className="h-5 w-5 text-white" />
                </span>

                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">
                  Подписывайтесь на&nbsp;наши каналы
                </span>
              </button>
            </div>
          </div>

          <p className="mx-auto mt-6 max-w-4xl text-center text-sm text-gray-600 dark:text-gray-400">
            При отправке заявки сайт обрабатывает имя, email и текст вопроса только для
            ответа на обращение.{" "}
            <a
              href="/privacy-policy"
              className="font-semibold text-violet-700 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-200"
            >
              Политика обработки персональных данных
            </a>
            .
          </p>
        </div>
      </footer>

      <ChannelsModal
        open={channelsOpen}
        onClose={() => setChannelsOpen(false)}
      />
    </>
  );
}

function ChannelsModal({ open, onClose }) {
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const t = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }

      if (e.key !== "Tab" || !panelRef.current) return;

      const focusables = Array.from(
        panelRef.current.querySelectorAll(
          "a,button,[tabindex]:not([tabindex='-1'])"
        )
      ).filter((el) => !el.hasAttribute("disabled"));

      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="footer-channels-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/55 dark:bg-black/70"
        aria-label="Закрыть окно выбора канала"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        className="
          relative w-full max-w-md
          rounded-3xl
          border border-gray-200 dark:border-gray-700
          bg-white dark:bg-gray-900
          shadow-2xl
          p-5 sm:p-6
        "
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2
              id="footer-channels-title"
              className="text-xl font-extrabold text-gray-900 dark:text-gray-100"
            >
              Выберите канал
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Подписывайтесь на новости и материалы про ИИ на Сахалине.
            </p>
          </div>

          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="
              shrink-0 inline-flex h-10 w-10 items-center justify-center
              rounded-xl
              border border-gray-200 dark:border-gray-700
              bg-white dark:bg-gray-950/40
              text-gray-700 dark:text-gray-200
              hover:bg-gray-100 dark:hover:bg-gray-800
              focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400
            "
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="mt-5 grid gap-3">
          <a
            href="https://t.me/AI_na_Sakhaline"
            target="_blank"
            rel="noreferrer"
            className="
              group flex items-center gap-4
              rounded-2xl p-4
              border border-gray-200 dark:border-gray-700
              bg-gray-50/80 dark:bg-gray-950/30
              hover:bg-violet-50 dark:hover:bg-white/5
              transition
              no-underline
              focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400
            "
            aria-label="Открыть телеграм-канал AI на Сахалине"
          >
            <span
              className="
                inline-flex h-12 w-12 items-center justify-center rounded-2xl
                text-white bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-700
                shrink-0
              "
              aria-hidden="true"
            >
              <TelegramIcon className="h-6 w-6 text-white" />
            </span>

            <span className="min-w-0">
              <span className="block font-extrabold text-gray-900 dark:text-gray-100">
                Telegram
              </span>
              <span className="block text-sm text-gray-600 dark:text-gray-400">
                AI на Сахалине
              </span>
            </span>
          </a>

          <a
            href="https://max.ru/id751902969999_biz"
            target="_blank"
            rel="noreferrer"
            className="
              group flex items-center gap-4
              rounded-2xl p-4
              border border-gray-200 dark:border-gray-700
              bg-gray-50/80 dark:bg-gray-950/30
              hover:bg-violet-50 dark:hover:bg-white/5
              transition
              no-underline
              focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400
            "
            aria-label="Открыть канал ИИ на Сахалине в Max"
          >
            <span
              className="
                inline-flex h-12 w-12 items-center justify-center rounded-2xl
                text-white bg-gradient-to-r from-violet-500 via-fuchsia-600 to-indigo-950
                shrink-0
              "
              aria-hidden="true"
            >
              <MaxIcon className="h-6 w-6 text-white" />
            </span>

            <span className="min-w-0">
              <span className="block font-extrabold text-gray-900 dark:text-gray-100">
                Max
              </span>
              <span className="block text-sm text-gray-600 dark:text-gray-400">
                ИИ на Сахалине
              </span>
            </span>
          </a>
        </div>
      </div>
    </div>
  );
}

function ChannelsIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M7.5 18.5 5 20v-3.2A6.8 6.8 0 0 1 3.2 12c0-4.4 4-8 8.8-8s8.8 3.6 8.8 8-4 8-8.8 8H10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M8 11.5h8M8 14.5h5.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TelegramIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M9.999 15.2 9.9 19c.4 0 .6-.2.8-.4l2-1.9 4.2 3.1c.8.4 1.3.2 1.5-.8l2.7-12.7c.3-1.2-.4-1.7-1.2-1.4L3.9 10.5c-1.2.5-1.2 1.2-.2 1.5l4.6 1.4 10.6-6.7c.5-.3.9-.1.5.2L9.999 15.2z" />
    </svg>
  );
}

function MaxIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M7.5 18.5 5 20v-3.2A6.8 6.8 0 0 1 3.2 12c0-4.4 4-8 8.8-8s8.8 3.6 8.8 8-4 8-8.8 8H10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M8 11.5h8M8 14.5h5.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
